import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
    WizardInitParamsDto,
    WizardInitResponseDto,
    ApplyBookingDto,
    ApplyBookingResponseDto,
    CreateShippingInstructionDto,
    UpdateShippingInstructionDto,
    ShippingInstructionCreatedDto,
    ShippingInstructionUpdatedDto,
    ShippingInstructionSoftDeletedDto,
    ShippingInstructionListResponseDto,
    ShippingInstructionDetailDto,
    WizardValidationResultDto,
    PartyDetailDto,
    AntecedentesCatalogsDto,
    ConditionCatalogItemDto,
    SpeciesCatalogItemDto,
    VarietyCatalogItemDto,
    ContainerPackageTypeDto,
    PalletPackageTypeDto,
    ColdTreatmentIndicatorDto,
    ControlledAtmosphereDto,
    VentilationUnitDto,
    SpeciesPackageTypeDto,
    CatalogItemDto,
} from '@opb-workspace/shared';
import { MOCK_WIZARD_INIT_617 } from '../mocks/wizard-init.mock';
import {
    MOCK_APPLY_BOOKING_FCL,
    MOCK_APPLY_BOOKING_LCL,
} from '../mocks/apply-booking.mock';
import {
    MOCK_PARTIES_SHIPPERS,
    MOCK_PARTIES_CONSIGNEES,
    MOCK_PARTIES_NOTIFIES,
    MOCK_VALIDATE_OK,
    MOCK_CREATE_SUCCESS,
} from '../mocks/parties-catalog.mock';
import {
    MOCK_CATALOGS,
    MOCK_CONTAINER_PACKAGE_TYPES,
    MOCK_COLD_TREATMENT_INDICATORS,
    MOCK_CONTROLLED_ATMOSPHERES,
    MOCK_VENTILATION_UNITS,
    MOCK_SPECIES_PACKAGE_TYPES,
    MOCK_PALLET_PACKAGE_TYPES,
    MOCK_WAY_OF_TRANSPORTS,
} from '../mocks/catalogs.mock';

@Injectable({ providedIn: 'root' })
export class InstructivoEmbarqueService {
    private readonly base = `${environment.apiUrl}/v1/instructivo-embarque`;

    constructor(private http: HttpClient) {}

    initWizard(params: WizardInitParamsDto): Observable<WizardInitResponseDto> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_WIZARD_INIT_617).pipe(delay(600));
        }
        return this.http.get<WizardInitResponseDto>(
            `${this.base}/wizard/init`,
            {
                params: {
                    voyageId: params.voyageId.toString(),
                    portOfLoadingId: params.portOfLoadingId.toString(),
                    portOfDischargeId: params.portOfDischargeId.toString(),
                },
            }
        );
    }

    applyBooking(dto: ApplyBookingDto): Observable<ApplyBookingResponseDto> {
        if (environment.useMocksInstructivoEmbarque) {
            const mock =
                dto.bookingId === 4588305
                    ? MOCK_APPLY_BOOKING_LCL
                    : MOCK_APPLY_BOOKING_FCL;
            return of(mock).pipe(delay(500));
        }
        return this.http.post<ApplyBookingResponseDto>(
            `${this.base}/wizard/apply-booking`,
            dto
        );
    }

    searchParties(
        role: 'shipper' | 'consignee' | 'notify',
        searchTerm: string
    ): Observable<PartyDetailDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            const map = {
                shipper: MOCK_PARTIES_SHIPPERS,
                consignee: MOCK_PARTIES_CONSIGNEES,
                notify: MOCK_PARTIES_NOTIFIES,
            };
            const filtered = map[role].filter((p) =>
                p.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
            return of(filtered).pipe(delay(200));
        }
        return this.http.get<PartyDetailDto[]>(`${this.base}/parties`, {
            params: { role, search: searchTerm },
        });
    }

    /** Aduana presentación u otras búsquedas genéricas de `tblParties`. */
    searchPartiesCustomsPresentation(searchTerm: string): Observable<
        PartyDetailDto[]
    > {
        if (environment.useMocksInstructivoEmbarque) {
            return of([]).pipe(delay(200));
        }
        return this.http.get<PartyDetailDto[]>(
            `${this.base}/parties/customs-presentation`,
            {
                params: {
                    search: searchTerm,
                },
            },
        );
    }

    getParties(
        role: 'shipper' | 'consignee' | 'notify'
    ): Observable<PartyDetailDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            const map = {
                shipper: MOCK_PARTIES_SHIPPERS,
                consignee: MOCK_PARTIES_CONSIGNEES,
                notify: MOCK_PARTIES_NOTIFIES,
            };
            return of(map[role]).pipe(delay(300));
        }
        return this.http.get<PartyDetailDto[]>(`${this.base}/parties`, {
            params: { role },
        });
    }

    getCatalogs(): Observable<AntecedentesCatalogsDto> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_CATALOGS).pipe(delay(300));
        }
        return this.http.get<AntecedentesCatalogsDto>(`${this.base}/catalogs`);
    }

    /** Vía de transporte — `sp_InstructivoEmbarque_GetWayOfTransports` (legacy). */
    getWayOfTransports(): Observable<CatalogItemDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_WAY_OF_TRANSPORTS).pipe(delay(200));
        }
        return this.http.get<CatalogItemDto[]>(
            `${this.base}/catalogs/way-of-transports`,
        );
    }

    getContainerPackageTypes(): Observable<ContainerPackageTypeDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_CONTAINER_PACKAGE_TYPES).pipe(delay(200));
        }
        return this.http.get<ContainerPackageTypeDto[]>(
            `${this.base}/catalogs/container-package-types`
        );
    }

    getPalletPackageTypes(): Observable<PalletPackageTypeDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_PALLET_PACKAGE_TYPES).pipe(delay(200));
        }
        return this.http.get<PalletPackageTypeDto[]>(
            `${this.base}/catalogs/pallet-package-types`
        );
    }

    getColdTreatmentIndicators(): Observable<ColdTreatmentIndicatorDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_COLD_TREATMENT_INDICATORS).pipe(delay(200));
        }
        return this.http.get<ColdTreatmentIndicatorDto[]>(
            `${this.base}/catalogs/cold-treatment-indicators`
        );
    }

    getControlledAtmospheres(): Observable<ControlledAtmosphereDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_CONTROLLED_ATMOSPHERES).pipe(delay(200));
        }
        return this.http.get<ControlledAtmosphereDto[]>(
            `${this.base}/catalogs/controlled-atmospheres`
        );
    }

    getVentilationUnits(): Observable<VentilationUnitDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_VENTILATION_UNITS).pipe(delay(200));
        }
        return this.http.get<VentilationUnitDto[]>(
            `${this.base}/catalogs/ventilation-units`
        );
    }

    getSpeciesPackageTypes(): Observable<SpeciesPackageTypeDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_SPECIES_PACKAGE_TYPES).pipe(delay(200));
        }
        return this.http.get<SpeciesPackageTypeDto[]>(
            `${this.base}/catalogs/species-package-types`
        );
    }

    getSpeciesCatalog(search?: string): Observable<SpeciesCatalogItemDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            return of([]);
        }
        return this.http.get<SpeciesCatalogItemDto[]>(
            `${this.base}/catalogs/species`,
            {
                params: search?.trim() ? { search: search.trim() } : {},
            }
        );
    }

    getVarietiesCatalog(
        merchandiseId?: number,
        search?: string
    ): Observable<VarietyCatalogItemDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            return of([]);
        }
        const params: Record<string, string> = {};
        if (merchandiseId != null) {
            params['merchandiseId'] = String(merchandiseId);
        }
        if (search?.trim()) {
            params['search'] = search.trim();
        }
        return this.http.get<VarietyCatalogItemDto[]>(
            `${this.base}/catalogs/varieties`,
            { params }
        );
    }

    getConditionsCatalog(search?: string): Observable<ConditionCatalogItemDto[]> {
        if (environment.useMocksInstructivoEmbarque) {
            return of([]);
        }
        return this.http.get<ConditionCatalogItemDto[]>(
            `${this.base}/catalogs/conditions`,
            {
                params: search?.trim() ? { search: search.trim() } : {},
            }
        );
    }

    validate(
        dto: Partial<CreateShippingInstructionDto>
    ): Observable<WizardValidationResultDto> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_VALIDATE_OK).pipe(delay(400));
        }
        return this.http.post<WizardValidationResultDto>(
            `${this.base}/wizard/validate`,
            dto
        );
    }

    create(
        dto: CreateShippingInstructionDto
    ): Observable<ShippingInstructionCreatedDto> {
        if (environment.useMocksInstructivoEmbarque) {
            return of(MOCK_CREATE_SUCCESS).pipe(delay(800));
        }
        return this.http.post<ShippingInstructionCreatedDto>(this.base, dto);
    }

    update(
        id: number,
        dto: UpdateShippingInstructionDto
    ): Observable<ShippingInstructionUpdatedDto> {
        return this.http.patch<ShippingInstructionUpdatedDto>(
            `${this.base}/${id}`,
            dto
        );
    }

    softDelete(id: number): Observable<ShippingInstructionSoftDeletedDto> {
        return this.http.delete<ShippingInstructionSoftDeletedDto>(
            `${this.base}/${id}`
        );
    }

    getList(filter: {
        voyageId?: number;
        search?: string;
        page?: number;
        size?: number;
    }): Observable<ShippingInstructionListResponseDto> {
        const params: Record<string, string> = {};
        if (filter.voyageId != null) params['voyageId'] = String(filter.voyageId);
        if (filter.search?.trim()) params['search'] = filter.search.trim();
        if (filter.page != null) params['page'] = String(filter.page);
        if (filter.size != null) params['size'] = String(filter.size);
        return this.http.get<ShippingInstructionListResponseDto>(this.base, { params });
    }

    getDetail(id: number): Observable<ShippingInstructionDetailDto> {
        return this.http.get<ShippingInstructionDetailDto>(`${this.base}/${id}`);
    }

    getPdfUrl(id: number): string {
        return `${this.base}/${id}/pdf`;
    }
}
